import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { QUERY_STALE_TIME, isSupabaseConfigured } from '@/config';
import { useAuth } from '@/lib/auth/AuthProvider';
import {
  buyStreakFreeze,
  dailyCheckin,
  getAdRewardState,
  getDailyMissions,
  getMyRecentPrize,
  getStreakDays,
  getWallet,
  grantAdReward,
  syncMyAchievements,
} from '@/lib/data/provider';
import { showRewardedAd } from '@/lib/ads/RewardedAds';

export function useWallet() {
  const { userId } = useAuth();
  return useQuery({
    queryKey: ['rewards', 'wallet', userId ?? 'anon'],
    queryFn: getWallet,
    staleTime: QUERY_STALE_TIME.user,
    enabled: isSupabaseConfigured && Boolean(userId),
  });
}

export function useDailyCheckin() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: dailyCheckin,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['rewards'] }),
  });
}

/** Spend coins for +1 streak freeze that auto-saves a single missed day. */
export function useBuyStreakFreeze() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: buyStreakFreeze,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['rewards'] }),
  });
}

/** Today's shared rewarded-ad budget (used/remaining/cap) across all placements.
 *  Keyed under ['rewards'] so every ad-grant mutation refreshes it. */
export function useAdRewardState() {
  const { userId } = useAuth();
  return useQuery({
    queryKey: ['rewards', 'adState', userId ?? 'anon'],
    queryFn: getAdRewardState,
    staleTime: QUERY_STALE_TIME.user,
    enabled: isSupabaseConfigured && Boolean(userId),
  });
}

/** Show a rewarded ad and, only if watched to completion, grant coins. */
export function useWatchAdForCoins() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const { earned } = await showRewardedAd();
      if (!earned) throw new Error('AD_NOT_COMPLETED');
      return grantAdReward();
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['rewards'] }),
  });
}

/** Today's repeatable missions with live progress + completion. */
export function useDailyMissions() {
  const { userId } = useAuth();
  return useQuery({
    queryKey: ['rewards', 'missions', userId ?? 'anon'],
    queryFn: getDailyMissions,
    staleTime: QUERY_STALE_TIME.user,
    enabled: isSupabaseConfigured && Boolean(userId),
  });
}

/** The caller's active days in the last 30 (for the streak calendar). */
export function useStreakDays() {
  const { userId } = useAuth();
  return useQuery({
    queryKey: ['rewards', 'streak-days', userId ?? 'anon'],
    queryFn: getStreakDays,
    staleTime: QUERY_STALE_TIME.user,
    enabled: isSupabaseConfigured && Boolean(userId),
  });
}

/** The caller's most recent weekly-prize win (for the Rewards banner). */
export function useMyRecentPrize() {
  const { userId } = useAuth();
  return useQuery({
    queryKey: ['rewards', 'recent-prize', userId ?? 'anon'],
    queryFn: getMyRecentPrize,
    staleTime: QUERY_STALE_TIME.user,
    enabled: isSupabaseConfigured && Boolean(userId),
  });
}

/** Evaluates + unlocks the caller's achievements (may award coins), returns all. */
export function useAchievements() {
  const { userId } = useAuth();
  const queryClient = useQueryClient();
  return useQuery({
    queryKey: ['rewards', 'achievements', userId ?? 'anon'],
    queryFn: async () => {
      const result = await syncMyAchievements();
      // A sync can mint coins for newly-unlocked badges - refresh the wallet.
      queryClient.invalidateQueries({ queryKey: ['rewards', 'wallet', userId ?? 'anon'] });
      return result;
    },
    staleTime: QUERY_STALE_TIME.user,
    enabled: isSupabaseConfigured && Boolean(userId),
  });
}
